import { ISbRichtext } from '@storyblok/react';
import { clsx } from 'clsx';
import { FC } from 'react';
import { Link } from 'atoms/Link/Link';
import { H3, P, Span } from 'atoms/Typography';
import { ReactMarkdown } from 'molecules/Markdown';
import { InView } from 'pages/ProductListing/InView';
import {
  gtmPushPromotionTileClick,
  gtmPushPromotionTileView,
} from 'modules/gtm/modules/sections/productList/promotionTile';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import { NameOfTile } from '../components/NameOfTile';
import { TileButton } from '../components/TileButton';
import { ITile, IsTitleExist, TileTypes } from '../types';
import styles from './DefaultTile.module.css';

export const DefaultTile: FC<
  ITile & {
    getText: (p: ISbRichtext) => string | undefined;
    isTitleExist: IsTitleExist;
  }
> = ({ blok, config, deviceType = 'mobile', getText, isTitleExist }) => {
  const { gtmData } = useSiteConfig();
  const backgroundUrl = blok[`image_${deviceType}`]?.filename;
  const { url = '#', classes, showName, css, buttonText } = config;
  const { wide_view, bottom_subTitle_1, bottom_subTitle_2 } = blok;
  const bottomSubTitlesColor = blok?.bottom_subTitles_color?.color || '#fff';

  const handleTileClick = () => {
    gtmPushPromotionTileClick({
      promotion_id: blok._uid,
      promotion_name: blok.name,
      location_id: gtmData?.pageType,
      creative_name: backgroundUrl,
      video_type: blok.type,
    });
  };

  return (
    <Link
      href={url}
      className={clsx(
        styles.root,
        !!blok?.topline?.length && styles.smallContainer,
        classes,
      )}
      style={{ backgroundImage: `url(${backgroundUrl})`, ...css }}
      data-tile-name={blok?.name}
      onClick={handleTileClick}
    >
      <InView
        className={styles.root}
        enable={blok.type === 'promotion' && !!gtmData?.pageType}
        onVisible={() => {
          gtmPushPromotionTileView({
            promotion_id: blok._uid,
            promotion_name: blok.name,
            location_id: gtmData?.pageType,
          });
        }}
      >
        {blok.type !== TileTypes.IMAGE && (
          <>
            <Span className={styles.content}>
              {blok?.topline && (
                <P className={styles.topLine}>{blok.topline}</P>
              )}
              {blok?.title && isTitleExist(blok.title) && (
                <H3
                  className={clsx(styles.title, wide_view && styles.wideTitle)}
                >
                  <ReactMarkdown
                    text={getText(blok.title)}
                    components={{
                      p: ({ children }) => (
                        <P className={styles.textLine}>{children}</P>
                      ),
                    }}
                  />
                </H3>
              )}
              {blok?.subtitle && (
                <Span className={styles.subtitle}>
                  <ReactMarkdown
                    text={getText(blok.subtitle)}
                    components={{
                      p: ({ children }) => (
                        <P className={styles.textLine}>{children}</P>
                      ),
                    }}
                  />
                </Span>
              )}
            </Span>
            <div className={styles.bottomSubTitle}>
              <P
                className={styles.bottomSubTitle_1}
                style={{ color: bottomSubTitlesColor }}
              >
                {bottom_subTitle_1}
              </P>
              <P
                className={styles.bottomSubTitle_2}
                style={{ color: bottomSubTitlesColor }}
              >
                {bottom_subTitle_2}
              </P>
            </div>
            <TileButton text={buttonText} isWide={wide_view} />
            {showName && <NameOfTile name={blok.name} />}
          </>
        )}
      </InView>
    </Link>
  );
};
